import { DurationString, getDurationStringSeconds } from '@chronosphereio/core';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { StringParam } from 'use-query-params';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { useOptionalQueryParamState } from '@/plugin-system/utils/url';

export const PrecedingPeriod = 'preceding-period';

export type CompareModeType = DurationString | typeof PrecedingPeriod | null;

interface Context {
  compareMode: CompareModeType;
  compareDurationInMs: number | null;
  setCompareMode: (duration: CompareModeType) => void;
}

export const CompareModeContext = createContext<Context | undefined>(undefined);

export const useCompareMode = () => {
  const ctx = useContext(CompareModeContext);
  if (ctx === undefined) {
    throw new Error('No CompareModeContext found. Did you forget a Provider?');
  }
  return ctx;
};

export const useCompareModeDurationInMs = () => {
  try {
    const ctx = useCompareMode();
    return ctx.compareDurationInMs;
  } catch (_e) {
    return null;
  }
};

const getCompareModeDurationInMs = (compareMode: CompareModeType, start: Date, end: Date) => {
  if (compareMode === null) {
    return null;
  } else if (compareMode === PrecedingPeriod) {
    return end.getTime() - start.getTime();
  } else {
    const duration = getDurationStringSeconds(compareMode);

    return duration === undefined ? null : duration * 1000;
  }
};

export const CompareModeProvider = ({
  children,
  initialValue,
  enableUrlParams = false,
}: {
  children: ReactNode;
  initialValue?: CompareModeType;
  enableUrlParams?: boolean;
}) => {
  const [compareMode, setRawCompareMode] = useOptionalQueryParamState<CompareModeType>({
    queryParamName: 'compare-to',
    initialValue: initialValue || null,
    enableQueryParam: enableUrlParams,
    paramConfig: StringParam as never,
  });

  const setCompareMode = useCallback(
    (value: CompareModeType) => {
      setRawCompareMode(value ?? null);
    },
    [setRawCompareMode]
  );

  const {
    absoluteTimeRange: { start, end },
  } = useTimeRange();

  const ctx = useMemo(
    () => ({
      compareDurationInMs: getCompareModeDurationInMs(compareMode, start, end),
      compareMode,
      setCompareMode,
    }),
    [compareMode, setCompareMode, start, end]
  );

  return <CompareModeContext.Provider value={ctx}>{children}</CompareModeContext.Provider>;
};
